import { Loader2, Mail } from "lucide-react";
import { useState } from "react";
import { useTranslation } from "react-i18next";

import { Button } from "@/components/ui/button";
import { AuthScreen } from "@/routes/auth-screen";
import { AuthField, TextInput } from "@/routes/auth-fields";

import { submitEnrolment } from "./api";

/**
 * The public enrolment page — the SPA's half of OAC-464 AC 9.
 *
 * It shows the refusal when the code does not resolve, as the Django page
 * does. AC 7 asks that the four causes produce "le même message de refus" —
 * one wording, not silence — and a participant who mistypes their code has to
 * find that out rather than wait for mail that will never arrive.
 *
 * What the page never reveals is *which* cause applied: the endpoint returns
 * one sentence for an unknown code, a closed group and a retired code alike,
 * and this page just renders it. A transport failure is shown the same way,
 * since the alternative is claiming a link was sent.
 *
 * A throttled attempt is the exception and gets its own sentence, as on the
 * Django page: it says nothing about the code, and the bad-code wording would
 * send someone to check a code that may be fine.
 */
export function EnrolmentPage() {
  const { t } = useTranslation();
  const [code, setCode] = useState("");
  const [email, setEmail] = useState("");
  const [status, setStatus] = useState<
    "idle" | "sending" | "sent" | "refused" | "throttled"
  >("idle");

  const emailValid = /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email.trim());
  const canSubmit = code.trim().length > 0 && emailValid;

  async function submit(e: React.FormEvent) {
    e.preventDefault();
    if (!canSubmit) return;
    setStatus("sending");
    // `catch`, not `finally`: a bare `finally` re-throws, and an async event
    // handler has nobody to throw to.
    try {
      const { sent, throttled } = await submitEnrolment({
        code: code.trim(),
        email: email.trim(),
      });
      setStatus(sent ? "sent" : throttled ? "throttled" : "refused");
    } catch {
      // A transport failure is shown as a refusal rather than as a sent link:
      // telling someone to go and wait for mail that was never sent is the
      // one answer that leaves them stuck.
      setStatus("refused");
    }
  }

  if (status === "sent") {
    return (
      <AuthScreen>
        <div className="text-center">
          <div className="mx-auto mb-3.5 grid size-12 place-items-center rounded-full bg-success/10">
            <Mail className="size-6 text-success" aria-hidden />
          </div>
          <h1 className="text-2xl font-extrabold">
            {t("enrolment.sentTitle")}
          </h1>
          <p className="mt-1.5 text-[14.5px] text-muted-foreground">
            {t("enrolment.sentBody")}
          </p>
        </div>
      </AuthScreen>
    );
  }

  return (
    <AuthScreen>
      <h1 className="text-2xl font-extrabold">{t("enrolment.title")}</h1>
      <p className="mt-1.5 text-[14.5px] text-muted-foreground">
        {t("enrolment.intro")}
      </p>
      {(status === "refused" || status === "throttled") && (
        <div
          role="alert"
          className="mt-4 rounded-md border border-destructive/30 bg-destructive/10 p-3 text-[14px] text-destructive-ink"
        >
          {t(
            status === "throttled" ? "enrolment.throttled" : "enrolment.refused",
          )}
        </div>
      )}
      <form noValidate onSubmit={submit}>
        <AuthField label={t("enrolment.codeLabel")} htmlFor="enrolment-code">
          <TextInput
            id="enrolment-code"
            type="text"
            autoComplete="off"
            value={code}
            onChange={setCode}
            placeholder={t("enrolment.codePlaceholder")}
          />
        </AuthField>
        <AuthField label={t("enrolment.emailLabel")} htmlFor="enrolment-email">
          <TextInput
            id="enrolment-email"
            type="email"
            autoComplete="email"
            value={email}
            onChange={setEmail}
            placeholder={t("enrolment.emailPlaceholder")}
          />
        </AuthField>
        <Button
          type="submit"
          className="mt-5 w-full"
          disabled={!canSubmit || status === "sending"}
        >
          {status === "sending" && (
            <Loader2 className="size-4 animate-spin" aria-hidden />
          )}
          {t("enrolment.submit")}
        </Button>
      </form>
    </AuthScreen>
  );
}
